import { computed, onMounted, ref } from 'vue';
import { QUALITY_RECORDS_ROOT_CODE, type NavigationItem } from '@/shared/lib';
import { listQualityRecordMatrices, type QualityRecordMatrix } from '@/entities/Matrix';
import { mapVisibleTreeToDocumentTypes } from './mapVisibleTreeToNavigation';
import { useVisibleDirectoryTree } from './useVisibleDirectoryTree';

export const QUALITY_RECORDS_ROOT_ITEM_ID = 'registros-qualidade';

export const matrixNavigationItemId = (prefix: string) => `matrix-${prefix}`;

const QUALITY_RECORDS_ROOT: Omit<NavigationItem, 'children'> = {
  id: QUALITY_RECORDS_ROOT_ITEM_ID,
  name: 'Registros da Qualidade',
  code: QUALITY_RECORDS_ROOT_CODE,
  type: 'document',
  icon: 'folder',
  expanded: false,
};

const mapMatrixToNavigationItem = (matrix: QualityRecordMatrix): NavigationItem => ({
  id: matrixNavigationItemId(matrix.prefix),
  name: matrix.code,
  code: matrix.code,
  type: 'category',
  icon: 'folder',
  metadata: { prefix: matrix.prefix, headMatrixId: matrix.headMatrixId },
});

export default function useDocumentTypes() {
  const { tree, load } = useVisibleDirectoryTree({ autoLoad: true });
  const matrices = ref<QualityRecordMatrix[]>([]);
  const loading = ref(false);
  const error = ref(false);

  const documentTypes = computed<NavigationItem[]>(() =>
    error.value
      ? []
      : [
          ...mapVisibleTreeToDocumentTypes(tree.value),
          { ...QUALITY_RECORDS_ROOT, children: matrices.value.map(mapMatrixToNavigationItem) },
        ]
  );

  const fetchDocumentTypes = async () => {
    loading.value = true;
    error.value = false;

    try {
      const [, loadedMatrices] = await Promise.all([load(), listQualityRecordMatrices()]);
      matrices.value = loadedMatrices;
    } catch {
      matrices.value = [];
      error.value = true;
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    fetchDocumentTypes();
  });

  return {
    documentTypes,
    loading,
    error,
    fetchDocumentTypes,
  };
}
